import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import AnimatedBackground from "@/components/AnimatedBackground";

export default function GatewayPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <>
      <AnimatedBackground />
      <div className="relative min-h-screen flex flex-col items-center justify-center px-6" style={{ zIndex: 1 }}>
        {/* Theme toggle - top right */}
        <button
          data-testid="gateway-theme-toggle"
          onClick={toggleTheme}
          className="theme-toggle fixed top-6 right-6 z-50"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          )}
        </button>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <img
            src={theme === "light" ? "/siveli-mark-transparent.png" : "/siveli-logo-dark.png"}
            alt="Siveli"
            className="h-16 sm:h-20 w-auto object-contain mx-auto"
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overline mb-12 text-center"
        >
          Engineering High Velocity People Function
        </motion.p>

        {/* Two doors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 w-full max-w-3xl">
          {/* Consulting */}
          <motion.button
            data-testid="gateway-consulting-btn"
            onClick={() => navigate("/consulting")}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="group glass-card p-8 sm:p-10 text-left cursor-pointer"
          >
            <span
              className="block text-xs tracking-[0.18em] uppercase mb-4"
              style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-gold)" }}
            >
              Advisory
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mb-4"
              style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
            >
              Consulting
            </h2>
            <p className="text-sm leading-relaxed text-themed-muted mb-8">
              Process diagnostics, capacity modeling, and Target Operating Model
              design for HR and People functions. The 3R Model — Reveal, Rewire, Rebuild.
            </p>
            <span
              className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300"
              style={{ color: "var(--sv-gold)" }}
            >
              Enter
              <ArrowRight
                size={16}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
            </span>
          </motion.button>

          {/* Apps */}
          <motion.button
            data-testid="gateway-apps-btn"
            onClick={() => navigate("/apps")}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="group glass-card p-8 sm:p-10 text-left cursor-pointer"
          >
            <span
              className="block text-xs tracking-[0.18em] uppercase mb-4"
              style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-copper)" }}
            >
              Products
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mb-4"
              style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
            >
              Apps
            </h2>
            <p className="text-sm leading-relaxed text-themed-muted mb-8">
              Purpose-built tools for the people function. Starting with Mitti —
              multilingual HR policy intelligence that answers from your documents.
            </p>
            <span
              className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300"
              style={{ color: "var(--sv-copper)" }}
            >
              Enter
              <ArrowRight
                size={16}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
            </span>
          </motion.button>
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-16 text-center text-sm italic"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-muted)" }}
        >
          "Measure before you transform."
        </motion.p>
      </div>
    </>
  );
}
